import type { Severity } from './types';
import './messageLibrary.css';

/** Which surface a severity puts the message on — the two-tier message standard
 *  (§18.6): Error / Warning / Confirm BLOCK in a centred dialog, Info / Success
 *  PASS in a transient toast. Both are CENTRED over the app: the shared
 *  `.ai-notify-bar` was revised to a compact centred popup on 2026-09-04, so a
 *  top-docked full-width bar would no longer be what the user actually sees. */
const SURFACE: Record<Severity, { kind: 'dialog' | 'toast'; icon: string; tone: string }> = {
  Error: { kind: 'dialog', icon: 'ti-alert-circle', tone: 'alert' },
  Warning: { kind: 'dialog', icon: 'ti-alert-triangle', tone: 'warn' },
  Confirm: { kind: 'dialog', icon: 'ti-help-circle', tone: 'info' },
  Info: { kind: 'toast', icon: 'ti-info-circle', tone: 'info' },
  Success: { kind: 'toast', icon: 'ti-circle-check', tone: 'ok' },
};

interface Props {
  /** The wording as currently typed, so the preview tracks edits live. */
  message: string;
  /** The chosen severity, or null when only a suggestion exists. */
  severity: Severity | null;
  /** What the API suggests from the wording, shown when none is chosen. */
  suggestion: Severity | null;
}

/**
 * Shows how a message will actually appear.
 *
 * Severity is the one field whose effect is invisible on the form itself: it
 * decides whether the message blocks the user in a centred dialog or slides past
 * in a transient toast. Drawing it removes the guesswork — the two do not look
 * alike, so the shape of the preview IS the explanation, which is why it carries
 * no caption.
 */
export function MessagePreview({ message, severity, suggestion }: Props) {
  const effective = severity ?? suggestion ?? 'Info';
  const surface = SURFACE[effective];

  return (
    <div className="ml-prev">
      <div className="ml-prev-h">Preview</div>

      {/* The STAGE — a stand-in for the screen behind the message. Without it a
          dialog reads as just another panel on the form; on a tinted surface it
          reads as floating over a page, which is what it does. Both surfaces sit
          in the MIDDLE of it, because both are centred over the app for real. */}
      <div className={`ml-prev-stage ${surface.kind}`}>
        {surface.kind === 'dialog' ? (
          <div className={`ml-prev-dialog ${surface.tone}`}>
            <i className={`ti ${surface.icon} ml-prev-icon`} />
            <div className="ml-prev-title">{effective}</div>
            <div className="ml-prev-msg">{message.trim() || <span className="ml-prev-empty">(empty message)</span>}</div>
            <div className="ml-prev-btns">
              {effective === 'Confirm' ? (
                <>
                  <span className="ml-prev-btn">No</span>
                  <span className="ml-prev-btn is-primary">Yes</span>
                </>
              ) : (
                <span className="ml-prev-btn is-primary">OK</span>
              )}
            </div>
          </div>
        ) : (
          <div className={`ml-prev-toast ${surface.tone}`}>
            <span className="ml-prev-tic"><i className={`ti ${surface.icon}`} /></span>
            <span>{message.trim() || <span className="ml-prev-empty">(empty message)</span>}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessagePreview;
